import React, {
    FocusEvent,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { PatternFormat } from 'react-number-format'
import { TextField } from '@mui/material'
import { AnyMaskedInputProps } from './AnyMaskedInput.types'
import { AnyComponentRef } from './AnyComponent.types'
import { InputValidator, TEXT_REQUIRED_VALIDATOR } from '../validators'

function AnyMakedInput({
    variant = 'outlined',
    size = 'small',
    margin = 'dense',
    format,
    mask = '_',
    fullWidth = true,
    name,
    label,
    initialValue,
    required,
    disabled,
    validators = [],
    register,
    notifier,
}: AnyMaskedInputProps) {
    const [blured, setBlured] = useState(false)
    const [valid, setValid] = useState(false)
    const [dirty, setDirty] = useState(false)
    const [error, setError] = useState<string>()
    const [value, setValue] = useState<string>(initialValue ?? '')
    const [formattedValue, setFormattedValue] = useState<string>()
    const allValidators = useMemo<InputValidator[]>(() => {
        let allValidators = [...validators]
        if (required) {
            allValidators = [TEXT_REQUIRED_VALIDATOR, ...validators]
        }

        return allValidators
    }, [required, validators])

    const ref = useRef<AnyComponentRef<string>>({
        valid,
        dirty,
        value,
        setBlur: () => setBlured(true),
    })

    const onMaskedInputChanged = (values: any) => {
        if (!values) return

        setDirty(true)
        setValue(values['value'])
        setFormattedValue(values['formattedValue'])
    }

    const inputBlured = (_event: FocusEvent<HTMLInputElement>) => {
        if (!blured) {
            setBlured(true)
        }
    }

    const onNotify = useCallback(
        (value?: string) => {
            if (notifier) {
                notifier(name, value)
            }
        },
        [notifier, name]
    )

    useEffect(() => {
        let valid = true
        let error: string | undefined
        for (const validator of allValidators) {
            if (!validator.isValid(value)) {
                valid = false
                error = validator.getMessage()
                break
            }
        }

        ref.current.valid = valid
        ref.current.dirty = dirty
        ref.current.value = value

        setValid(valid)
        setError(error)

        onNotify(value)
    }, [value, dirty, allValidators, onNotify])

    useEffect(() => {
        register(name, ref.current)
    }, [register, name, ref])

    useEffect(() => {
        setValue(initialValue ?? '')
        setFormattedValue(initialValue ?? '')
    }, [initialValue])

    return (
        <PatternFormat
            customInput={TextField}
            variant={variant}
            size={size}
            margin={margin}
            name={name}
            label={label}
            value={formattedValue}
            defaultValue={formattedValue}
            required={required}
            disabled={disabled}
            fullWidth={fullWidth}
            error={blured && !valid}
            helperText={blured ? error : undefined}
            onBlur={inputBlured}
            format={format}
            mask={mask}
            onValueChange={onMaskedInputChanged}
        />
    )
}

export default memo(AnyMakedInput)
